import styled, { css } from 'styled-components';
import { useBrowserWindowContext } from '../../components/BrowserWindowContext';
import { useState, createContext, useContext, useCallback, useRef, useLayoutEffect } from 'react';
import { handleIframeResize } from '../../post-message/handleIframeResize';
import { handleRestoreSavedLauncherPosition } from '../../post-message/handleRestoreSavedLauncherPosition';
import { handleResetToDefaultLauncherPosition } from '../../post-message/handleResetToDefaultLauncherPosition';
import { useMeasure } from '../../hooks/useMeasure';
import { LEFT_ALIGNED, RIGHT_ALIGNED } from 'conversations-internal-schema/widget-location/constants/WidgetLocations';
import { SIDE_DOCK_WIDTH } from '../constants/sideDockConstants';
import { jsx as _jsx } from "react/jsx-runtime";
const widgetLocationStyles = {
  [RIGHT_ALIGNED]: css(["right:0;align-items:flex-end;"]),
  [LEFT_ALIGNED]: css(["left:0;align-items:flex-start;"])
};
const Wrapper = styled.div.withConfig({
  displayName: "SideDockWidgetWrapper__Wrapper"
})(["position:absolute;display:flex;flex-direction:column;", ";"], ({
  $isFullScreen,
  widgetLocation
}) => $isFullScreen ? css(["top:0;left:0;right:0;bottom:0;"]) : css(["bottom:0;padding:16px;gap:12px;justify-content:flex-end;", ";"], widgetLocationStyles[widgetLocation]));
// TODO: bkopp consider moving this state outside of Context as it's not preferred per HubSpot best practices.
export const SideDockContext = /*#__PURE__*/createContext({
  onCloseAnimationComplete: () => {},
  isAnimationReady: false
});
export const useSideDockContext = () => useContext(SideDockContext);
export default function SideDockWidgetWrapper({
  children,
  isOpen,
  widgetLocation
}) {
  const {
    browserWindowHeight,
    browserWindowWidth
  } = useBrowserWindowContext();
  const measureRef = useMeasure();
  const wrapperRef = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(isOpen);
  const [isAnimationReady, setIsAnimationReady] = useState(false);
  const onCloseAnimationComplete = useCallback(() => {
    setIsFullScreen(false);
    setIsAnimationReady(false);
  }, []);
  useLayoutEffect(() => {
    if (isOpen) {
      setIsFullScreen(true);
      handleResetToDefaultLauncherPosition();
      handleIframeResize({
        width: SIDE_DOCK_WIDTH,
        height: browserWindowHeight
      });
      let rafId1 = null;
      let rafId2 = null;
      rafId1 = requestAnimationFrame(() => {
        rafId2 = requestAnimationFrame(() => {
          setIsAnimationReady(true);
        });
      });
      return () => {
        if (rafId1 !== null) cancelAnimationFrame(rafId1);
        if (rafId2 !== null) cancelAnimationFrame(rafId2);
      };
    }
  }, [isOpen, browserWindowHeight, browserWindowWidth]);
  useLayoutEffect(() => {
    if (!isFullScreen && wrapperRef.current) {
      const rafId = requestAnimationFrame(() => {
        if (wrapperRef.current) {
          const {
            offsetWidth,
            offsetHeight
          } = wrapperRef.current;
          handleIframeResize({
            width: offsetWidth,
            height: offsetHeight
          });
          handleRestoreSavedLauncherPosition();
        }
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [isFullScreen]);
  const style = isFullScreen ? {
    height: browserWindowHeight,
    width: SIDE_DOCK_WIDTH
  } : {};
  const combinedRef = useCallback(node => {
    wrapperRef.current = node;
    if (!isFullScreen) {
      measureRef(node);
    }
  }, [isFullScreen, measureRef]);
  return /*#__PURE__*/_jsx(SideDockContext.Provider, {
    value: {
      onCloseAnimationComplete,
      isAnimationReady
    },
    children: /*#__PURE__*/_jsx(Wrapper, {
      ref: combinedRef,
      style: style,
      widgetLocation: widgetLocation,
      $isFullScreen: isFullScreen,
      "data-test-id": "side-dock-widget-wrapper",
      children: children
    })
  });
}
SideDockWidgetWrapper.displayName = 'SideDockWidgetWrapper';